import React, {
	useEffect,
	useRef,
	Suspense,
	lazy,
	Component,
	ErrorInfo,
	ReactNode,
} from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Analytics } from "@vercel/analytics/react";
import Header from "./components/Header";
import Footer from "./components/Footer";

// Lazy load all pages for better performance
const HomePage = lazy(() => import("./pages/HomePage"));
const GuideLibraryPage = lazy(() => import("./pages/GuideLibraryPage"));
const GuideViewPage = lazy(() => import("./pages/GuideViewPage"));
const EditGuidePage = lazy(() => import("./pages/EditGuidePage"));
const SubmitGuidePage = lazy(() => import("./pages/SubmitGuidePage"));
const ForumPage = lazy(() => import("./pages/ForumPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const JoinCommunityPage = lazy(() => import("./pages/JoinCommunityPage"));
const WritingGuidePage = lazy(() => import("./pages/WritingGuidePage"));
const AffiliateGuidePage = lazy(() => import("./pages/AffiliateGuidePage"));
const RewardsPage = lazy(() => import("./pages/RewardsPage"));
const LeaderboardPage = lazy(() => import("./pages/LeaderboardPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const BugReportPage = lazy(() => import("./pages/BugReportPage"));
const SuggestionsPage = lazy(() => import("./pages/SuggestionsPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const CategoryManagerPage = lazy(() => import("./pages/CategoryManagerPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const AdminContactFormsPage = lazy(
	() => import("./pages/AdminContactFormsPage")
);
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const SharingPage = lazy(() => import("./pages/SharingPage"));
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { activityService } from "./services/activityService";
import { PreviewService } from "./services/previewService";
import {
	collection,
	query,
	where,
	getDocs,
	deleteDoc,
} from "firebase/firestore";
import { db } from "./firebase";
import CookieBanner from "./components/CookieBanner";
import OnboardingGate from "./components/OnboardingGate";

// Error Boundary Component
interface ErrorBoundaryState {
	hasError: boolean;
	error?: Error;
}

class ErrorBoundary extends Component<
	{ children: ReactNode },
	ErrorBoundaryState
> {
	constructor(props: { children: ReactNode }) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		// Log error to console in development
		if (process.env.NODE_ENV === "development") {
			console.error("Error caught by boundary:", error, errorInfo);
		}

		// Suppress extension-related errors
		if (
			error.message?.includes("Could not establish connection") ||
			error.message?.includes("Receiving end does not exist") ||
			error.message?.includes("Params are not set")
		) {
			// These are likely extension errors, ignore them
			this.setState({ hasError: false });
			return;
		}
	}

	render() {
		if (this.state.hasError) {
			return (
				<div className="min-h-screen bg-slate-900 text-slate-200 flex items-center justify-center">
					<div className="text-center p-8">
						<h1 className="text-2xl font-bold text-red-400 mb-4">
							אירעה שגיאה
						</h1>
						<p className="text-slate-300 mb-4">
							מצטערים, אירעה שגיאה לא צפויה.
						</p>
						<button
							onClick={() => window.location.reload()}
							className="px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-500"
						>
							רענן דף
						</button>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

const ActivityTracker: React.FC = () => {
	const { user } = useAuth();
	const lastActivityRef = useRef<number>(Date.now());
	const intervalRef = useRef<number | null>(null);

	useEffect(() => {
		const bump = () => {
			lastActivityRef.current = Date.now();
		};
		window.addEventListener("mousemove", bump);
		window.addEventListener("keydown", bump);
		window.addEventListener("scroll", bump, { passive: true });

		const tick = async () => {
			if (!user) return;
			const now = Date.now();
			if (document.visibilityState !== "visible") return;
			// consider active if interacted within last 120s
			if (now - lastActivityRef.current <= 120_000) {
				try {
					await activityService.incrementActiveMinutes(user.uid, 1); // +1 minute
				} catch (e) {
					console.error("activity minute update failed", e);
				}
			}
		};

		intervalRef.current = window.setInterval(tick, 60_000);
		return () => {
			window.removeEventListener("mousemove", bump);
			window.removeEventListener("keydown", bump);
			window.removeEventListener("scroll", bump as any);
			if (intervalRef.current) window.clearInterval(intervalRef.current);
		};
	}, [user]);

	// Global like synchronization
	useEffect(() => {
		const handleLikeSync = async (event: CustomEvent) => {
			// Broadcast like event to all components
			window.dispatchEvent(new CustomEvent("likeSyncRequest"));
		};

		window.addEventListener(
			"userLikedContent",
			handleLikeSync as EventListener
		);

		return () => {
			window.removeEventListener(
				"userLikedContent",
				handleLikeSync as EventListener
			);
		};
	}, []);

	return null;
};

const App: React.FC = () => {
	// Global error handler to suppress extension errors
	useEffect(() => {
		const handleError = (event: ErrorEvent) => {
			// Suppress extension-related errors
			if (
				event.message?.includes("Could not establish connection") ||
				event.message?.includes("Receiving end does not exist") ||
				event.message?.includes("Params are not set") ||
				event.filename?.includes("contentScript.js") ||
				event.filename?.includes("mf.js")
			) {
				event.preventDefault();
				return false;
			}
		};

		const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
			// Suppress extension-related promise rejections
			if (
				event.reason?.message?.includes("Could not establish connection") ||
				event.reason?.message?.includes("Receiving end does not exist") ||
				event.reason?.message?.includes("Params are not set")
			) {
				event.preventDefault();
				return false;
			}
		};

		window.addEventListener("error", handleError);
		window.addEventListener("unhandledrejection", handleUnhandledRejection);

		return () => {
			window.removeEventListener("error", handleError);
			window.removeEventListener(
				"unhandledrejection",
				handleUnhandledRejection
			);
		};
	}, []);

	// Cleanup expired preview guides on app start
	useEffect(() => {
		const cleanupPreviews = async () => {
			try {
				await PreviewService.cleanupExpiredPreviews();
			} catch (error) {
				console.error("Failed to cleanup expired previews:", error);
			}
		};

		// Run cleanup on app start
		cleanupPreviews();

		// Set up weekly cleanup (every 7 days)
		const interval = setInterval(cleanupPreviews, 7 * 24 * 60 * 60 * 1000);

		return () => clearInterval(interval);
	}, []);

	// One-time cleanup of expired preview guides
	useEffect(() => {
		const cleanupExpiredPreviews = async () => {
			try {
				const guidesRef = collection(db, "guides");
				const previewQuery = query(guidesRef, where("isPreview", "==", true));
				const snapshot = await getDocs(previewQuery);

				if (!snapshot.empty) {
					const now = new Date();
					const expiredGuides = [];

					snapshot.docs.forEach((doc) => {
						const data = doc.data();
						const expiresAt =
							data.previewExpiresAt?.toDate?.() || data.previewExpiresAt;
						if (expiresAt && now > expiresAt) {
							expiredGuides.push(doc);
						}
					});

					if (expiredGuides.length > 0) {
						console.log(
							`Found ${expiredGuides.length} expired preview guides that need cleanup`
						);

						// Delete only expired preview guides
						const deletePromises = expiredGuides.map((doc) =>
							deleteDoc(doc.ref)
						);
						await Promise.all(deletePromises);

						console.log(
							`Cleaned up ${expiredGuides.length} expired preview guides`
						);
					}
				}
			} catch (error) {
				console.error("Failed to cleanup expired previews:", error);
			}
		};

		// Run once when app starts
		cleanupExpiredPreviews();
	}, []);

	const ScrollToTop: React.FC = () => {
		const { pathname, hash } = useLocation();
		useEffect(() => {
			window.scrollTo({ top: 0, behavior: "smooth" });
		}, [pathname, hash]);
		return null;
	};

	const GoogleAnalytics: React.FC = () => {
		const { pathname, hash } = useLocation();

		useEffect(() => {
			// Track page view when route changes
			if (typeof window !== "undefined" && window.gtag) {
				const fullPath = pathname + hash;
				window.gtag("config", "G-MBPLVSSWDY", {
					page_path: fullPath,
				});
			}
		}, [pathname, hash]);

		return null;
	};

	return (
		<HelmetProvider>
			<AuthProvider>
				<ErrorBoundary>
					<BrowserRouter>
						<div className="flex flex-col min-h-screen font-sans max-w-full overflow-x-hidden">
							<ActivityTracker />
							<ScrollToTop />
							<GoogleAnalytics />
							<OnboardingGate />
							<CookieBanner />
							<Header />
							<main className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-full overflow-x-hidden">
								<Suspense
									fallback={
										<div className="flex justify-center items-center h-64">
											<div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-cyan-500"></div>
											<p className="ml-4 text-slate-400">טוען...</p>
										</div>
									}
								>
									<Routes>
										<Route path="/" element={<HomePage />} />
										<Route path="/guides" element={<GuideLibraryPage />} />
										<Route
											path="/guides/:identifier"
											element={<GuideViewPage />}
										/>
										<Route
											path="/guides/:identifier/edit"
											element={
												<ProtectedRoute>
													<EditGuidePage />
												</ProtectedRoute>
											}
										/>
										<Route path="/forum" element={<ForumPage />} />
										<Route path="/forum/:categoryId" element={<ForumPage />} />
										<Route
											path="/forum/:categoryId/:postId"
											element={<ForumPage />}
										/>
										<Route path="/login" element={<LoginPage />} />
										{/* Community Pages */}
										<Route path="/join" element={<JoinCommunityPage />} />
										<Route
											path="/writing-guide"
											element={<WritingGuidePage />}
										/>
										<Route path="/rewards" element={<RewardsPage />} />
										<Route path="/leaderboard" element={<LeaderboardPage />} />
										{/* Support Pages */}
										<Route path="/contact" element={<ContactPage />} />
										<Route path="/bug-report" element={<BugReportPage />} />
										<Route path="/suggestions" element={<SuggestionsPage />} />
										<Route path="/faq" element={<FAQPage />} />
										<Route
											path="/affiliate-guide"
											element={<AffiliateGuidePage />}
										/>
										{/* Legal Pages */}
										<Route path="/terms" element={<TermsOfServicePage />} />
										<Route path="/privacy" element={<PrivacyPolicyPage />} />
										{/* Protected Routes */}
										<Route
											path="/submit"
											element={
												<ProtectedRoute>
													<SubmitGuidePage />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/profile"
											element={
												<ProtectedRoute>
													<ProfilePage />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/notifications"
											element={
												<ProtectedRoute>
													<NotificationsPage />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/admin"
											element={
												<ProtectedRoute requireAdmin>
													<AdminDashboardPage />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/admin/categories"
											element={
												<ProtectedRoute requireAdmin>
													<CategoryManagerPage />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/admin/contact-forms"
											element={
												<ProtectedRoute requireAdmin>
													<AdminContactFormsPage />
												</ProtectedRoute>
											}
										/>
										<Route path="/sharing" element={<SharingPage />} />
										<Route path="/sharing/:itemId" element={<SharingPage />} />
									</Routes>
								</Suspense>
							</main>
							<Footer />
						</div>
						<Analytics />
					</BrowserRouter>
				</ErrorBoundary>
			</AuthProvider>
		</HelmetProvider>
	);
};

export default App;
